import { useQuery, useSubscription } from '@apollo/client/react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { GET_VIDEO_CONFERENCE, LIST_STAFF } from '../../graphql/queries';
import { ON_WAITING_ROOM_UPDATED } from '../../graphql/videoConferences';
import { ChatTranscript } from './ChatTranscript';
import { format } from 'date-fns';

interface Participant {
  id: string;
  type: 'CLIENT' | 'INQUIRY' | 'GUEST';
  referenceId?: string;
  name: string;
  email?: string;
  phone?: string;
  notifyEmail: boolean;
  notifyPush: boolean;
  notifySms: boolean;
}

interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

interface VideoConference {
  id: string;
  title: string;
  description?: string;
  status: string;
  scheduledStartTime: string;
  scheduledEndTime: string;
  actualStartTime?: string;
  actualEndTime?: string;
  duration?: number;
  hostId: string;
  host?: { id: string; firstName: string; lastName: string };
  isRecordingEnabled: boolean;
  recordingStatus?: string;
  waitingRoomEnabled?: boolean;
  reminderTimes?: number[];
  attendeeIds?: string[];
  participants?: Participant[];
  clientId?: string;
  client?: { id: string; firstName: string; lastName: string; email?: string };
  inquiryId?: string;
  inquiry?: { id: string; firstName: string; lastName: string; email?: string };
  guestName?: string;
  guestEmail?: string;
  guestPhone?: string;
}

interface ConferenceDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  conferenceId: string;
}

const REMINDER_LABELS: Record<number, string> = {
  10080: '1 week',
  4320: '3 days',
  1440: '1 day',
  720: '12 hours',
  360: '6 hours',
  180: '3 hours',
  60: '1 hour',
  30: '30 minutes',
  15: '15 minutes',
  10: '10 minutes',
  5: '5 minutes',
  0: 'At start time',
};

export function ConferenceDetailModal({
  isOpen,
  onClose,
  conferenceId,
}: ConferenceDetailModalProps) {
  const { data: conferenceData, loading, refetch } = useQuery<{ getVideoConference: VideoConference }>(
    GET_VIDEO_CONFERENCE,
    {
      variables: { id: conferenceId },
      skip: !conferenceId || !isOpen,
      fetchPolicy: 'cache-and-network',
    }
  );

  const { data: staffData } = useQuery<{ listStaff: StaffMember[] }>(LIST_STAFF);
  const staffMembers = staffData?.listStaff || [];

  const conference = conferenceData?.getVideoConference;

  // Subscribe to waiting room updates for real-time notifications
  useSubscription<{ onWaitingRoomUpdated: { id: string; waitingRoomParticipants: any[] } }>(ON_WAITING_ROOM_UPDATED, {
    variables: { conferenceId },
    skip: !conferenceId || !isOpen || conference?.status === 'COMPLETED' || conference?.status === 'CANCELLED',
    onData: ({ data: subData }) => {
      // Refetch to get updated waiting room participants
      if (subData?.data?.onWaitingRoomUpdated) {
        refetch();
      }
    },
  });

  // Get waiting room count
  const waitingRoomCount = (conference as any)?.waitingRoomParticipants?.length || 0;

  if (!isOpen) return null;

  const getStatusBadgeVariant = (status: string): 'info' | 'success' | 'secondary' | 'destructive' | 'warning' => {
    const variants: Record<string, 'info' | 'success' | 'secondary' | 'destructive' | 'warning'> = {
      SCHEDULED: 'info',
      IN_PROGRESS: 'success',
      COMPLETED: 'secondary',
      CANCELLED: 'destructive',
      NO_SHOW: 'warning',
    };
    return variants[status] || 'secondary';
  };

  const getParticipantTypeColor = (type: string) => {
    switch (type) {
      case 'CLIENT': return 'bg-red-100 text-red-700';
      case 'INQUIRY': return 'bg-blue-100 text-blue-700';
      case 'GUEST': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDuration = (minutes?: number): string => {
    if (!minutes) return '—';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const getAttendeeNames = () => {
    if (!conference?.attendeeIds || conference.attendeeIds.length === 0) return [];
    return conference.attendeeIds.map(id => {
      const staff = staffMembers.find(s => s.id === id);
      return staff ? `${staff.firstName} ${staff.lastName}` : 'Unknown';
    });
  };

  const getReminderLabels = () => {
    if (!conference?.reminderTimes || conference.reminderTimes.length === 0) return ['None'];
    return conference.reminderTimes.map(t => REMINDER_LABELS[t] || `${t} minutes`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900">Conference Details</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aalaw-red"></div>
            </div>
          ) : !conference ? (
            <div className="text-center py-12 text-gray-500">Conference not found</div>
          ) : (
            <div className="space-y-6">
              {/* Title & Status */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{conference.title}</h3>
                  {conference.description && (
                    <p className="mt-1 text-sm text-gray-600">{conference.description}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Badge variant={getStatusBadgeVariant(conference.status)} className="ml-4">
                    {conference.status.replace('_', ' ')}
                  </Badge>
                  {/* Waiting Room Indicator */}
                  {conference.status === 'SCHEDULED' && waitingRoomCount > 0 && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-full">
                      <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <span className="text-xs font-medium text-amber-700">
                        {waitingRoomCount} waiting
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Schedule Section */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Schedule
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Start Time</p>
                    <p className="text-sm font-medium text-gray-900">
                      {format(new Date(conference.scheduledStartTime), 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">End Time</p>
                    <p className="text-sm font-medium text-gray-900">
                      {format(new Date(conference.scheduledEndTime), 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                  {conference.actualStartTime && (
                    <div>
                      <p className="text-xs text-gray-500">Actual Start</p>
                      <p className="text-sm font-medium text-gray-900">
                        {format(new Date(conference.actualStartTime), 'MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                  )}
                  {conference.duration && (
                    <div>
                      <p className="text-xs text-gray-500">Duration</p>
                      <p className="text-sm font-medium text-gray-900">{formatDuration(conference.duration)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Host Section */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Host
                </h4>
                <p className="text-sm font-medium text-gray-900">
                  {conference.host 
                    ? `${conference.host.firstName} ${conference.host.lastName}`
                    : 'Unknown'}
                </p>
              </div>

              {/* Participants Section */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Participants ({conference.participants?.length || 0})
                </h4>
                {conference.participants && conference.participants.length > 0 ? (
                  <div className="space-y-3">
                    {conference.participants.map((p, idx) => (
                      <div key={p.id || idx} className="flex items-start justify-between bg-white rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-600">
                            {p.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{p.name}</p>
                            {p.email && <p className="text-xs text-gray-500">{p.email}</p>}
                            {p.phone && <p className="text-xs text-gray-500">{p.phone}</p>}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getParticipantTypeColor(p.type)}`}>
                            {p.type}
                          </span>
                          <div className="flex gap-1">
                            {p.notifyEmail && (
                              <span className="text-xs text-gray-400" title="Email notifications">📧</span>
                            )}
                            {p.notifySms && (
                              <span className="text-xs text-gray-400" title="SMS notifications">📱</span>
                            )}
                            {p.notifyPush && (
                              <span className="text-xs text-gray-400" title="Push notifications">🔔</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No participants</p>
                )}
              </div>

              {/* Staff Attendees Section */}
              {conference.attendeeIds && conference.attendeeIds.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Staff Attendees ({conference.attendeeIds.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {getAttendeeNames().map((name, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-700"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Settings Section */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Settings
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-full ${conference.isRecordingEnabled ? 'bg-red-500' : 'bg-gray-300'}`}></span>
                    <span className="text-sm text-gray-700">
                      Recording {conference.isRecordingEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-full ${conference.waitingRoomEnabled ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                    <span className="text-sm text-gray-700">
                      Waiting Room {conference.waitingRoomEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  {conference.recordingStatus && (
                    <div>
                      <p className="text-xs text-gray-500">Recording Status</p>
                      <p className="text-sm font-medium text-gray-900">{conference.recordingStatus}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Reminders Section */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  Reminder Notifications
                </h4>
                <div className="flex flex-wrap gap-2">
                  {getReminderLabels().map((label, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-700"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </div>

              {/* Chat Transcript Section (only for completed conferences) */}
              {conference.status === 'COMPLETED' && (
                <ChatTranscript 
                  conferenceId={conferenceId} 
                  conferenceStatus={conference.status} 
                />
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t border-gray-200 bg-gray-50">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
